<template>
  <div class="w-full flex flex-col items-center justify-center min-h-[100px] py-24 mb-32">
    <!-- Header -->
    <div class="w-full text-gray-900 text-16 flex pb-12 justify-between">
      <div>Carbon Emissions</div>
      <p
        class="text-14 text-primary-500 hover:cursor-pointer hover:text-primary-600"
        @click="handleDownload"
        data-pdf-hide
      >
        <i class="fa-solid fa-download mr-4"></i>Download Chart
      </p>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-full">
      <SparkSpinner />
    </div>
    <div v-else class="w-full relative">
      <div class="relative">
        <div class="text-12 font-sans text-left w-full flex justify-between mb-16">
          <span class="text-gray-600">Carbon Emission Saved: </span>
          <div>
            <span class="text-black">{{ differenceMainBechmarkValue }}</span>
            <span class="text-gray-400" v-html="` ${formattedTotalUnit}`"></span>
          </div>
        </div>
        <hr />
        <div class="space-y-6 pt-16">
          <div v-for="(chain, chainIndex) in processedChains" :key="chainIndex" class="w-full mb-20">
            <div class="mb-2">
              <h3 class="text-12 text-gray-500">{{ chain.name }}</h3>
              <span
                class="text-12 text-gray-500 font-sans w-1/5 text-right"
                :style="{ fontWeight: 'normal' }"
                v-html="`Total: ${chain.total} ${formattedTotalUnit}`"
              ></span>
            </div>

            <!-- Chart -->
            <div class="h-16 mb-4">
              <Bar :ref="`chartRef-${chainIndex}`" :data="chain.chartData" :options="chain.chartOptions" />
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="legendItems && legendItems.length" class="w-full mt-8 bg-white">
        <!-- Total Row -->
        <div class="flex items-center mb-2" style="height: 25px">
          <span class="text-12 text-navigation font-sans">Legend</span>
        </div>

        <!-- Separator Line -->
        <div class="w-full h-px bg-gray-200 my-2"></div>

        <!-- Legend Items -->
        <div class="grid grid-cols-2 gap-2">
          <div v-for="item in legendItems" :key="item.label" class="flex items-center" style="height: 25px">
            <!-- Color Box -->
            <div
              class="flex-shrink-0"
              :style="{
                width: '15px',
                height: '15px',
                backgroundColor: item.color,
                marginRight: '10px',
              }"
            ></div>
            <!-- Label -->
            <span class="text-12 text-navigation font-sans capitalize" :title="item.label">
              {{ item.label }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import logoUrl from '@/assets/img/Logo_Positive_Transparent.png';
import {
  getAllSegmentTypes,
  getMaxTotal,
  calculateTotal,
  sortSegments,
  formatCO2Unit,
  differenceMainBechmark,
} from '@/utils/carbonCalculation';
import {
  processMultipleChains,
  generateMultipleChainLegend,
  getSegmentColor,
  CUSTOM_COLOR_PALETTE,
} from '@/utils/carbonChartUtils';
import { mapState } from 'vuex';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default {
  name: 'CarbonChartComparisonChain',
  components: { Bar, SparkSpinner },
  data() {
    return {
      selectedCarbonLevel: { _pl: 'Lot' },
    };
  },
  props: {
    processChains: {
      type: Array,
      required: true,
      // Expected format: [{ name: string, segments: [{ label, value, tooltip }] }]
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalUnit: {
      type: String,
      default: 'kg CO2',
    },
  },
  computed: {
    ...mapState(['part']),

    allSegmentTypes() {
      return getAllSegmentTypes(this.processChains);
    },
    maxTotal() {
      return getMaxTotal(this.processChains);
    },
    legendItems() {
      return generateMultipleChainLegend(this.processChains);
    },
    processedChains() {
      return processMultipleChains(this.processChains, this.totalUnit);
    },
    formattedTotalUnit() {
      return formatCO2Unit(this.totalUnit);
    },
    differenceMainBechmarkValue() {
      let mainProcessChainUid = this.part.primary_process_chain_id;
      let benchmarkProcessChainUid = this.part.benchmark_process_chain_id;
      return differenceMainBechmark(
        this.part.process_chains[mainProcessChainUid],
        this.part.process_chains[benchmarkProcessChainUid],
        this.selectedCarbonLevel
      );
    },
  },
  methods: {
    async handleDownload() {
      // Create a composite canvas for all charts
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d');

      // Constants
      const padding = 20;
      const chartHeight = 60; // Height per chart
      const chartSpacing = 40; // Space between charts
      const carbonSavedHeight = 55; // Height for Carbon Emission Saved section
      const partNameHeight = 40; // Height for part name section
      const legendHeight = 80;

      const baseWidth = 800;
      const totalHeight =
        carbonSavedHeight +
        partNameHeight +
        this.processChains.length * chartHeight +
        (this.processChains.length - 1) * chartSpacing +
        legendHeight +
        padding * 3;

      // Set canvas size with scale for quality
      const scale = 2;
      exportCanvas.width = baseWidth * scale;
      exportCanvas.height = totalHeight * scale;
      exportCtx.scale(scale, scale);

      // Fill background
      exportCtx.fillStyle = '#ffffff';
      exportCtx.fillRect(0, 0, baseWidth, totalHeight);

      let currentY = padding;

      // Draw part name in bold at the top
      exportCtx.fillStyle = '#000000'; // black color
      exportCtx.font = 'bold 16px Poppins';
      exportCtx.textAlign = 'center';
      exportCtx.fillText(this.part.name || 'Part', baseWidth / 2, currentY + 20);
      exportCtx.textAlign = 'left'; // Reset alignment for following sections
      currentY += 50;

      // Draw Carbon Emission Saved section
      exportCtx.fillStyle = '#6B7280'; // text-gray-600
      exportCtx.font = '12px Poppins';
      exportCtx.textAlign = 'left';
      exportCtx.fillText('Carbon Emission Saved:', padding, currentY + 15);

      // Draw value and unit on the right
      const formattedUnit = this.totalUnit.replace(/CO2|Co2|co2/gi, 'CO₂');
      const valueText = `${this.differenceMainBechmarkValue} ${formattedUnit}`;

      exportCtx.fillStyle = '#000000'; // text-black
      exportCtx.font = '12px Poppins';
      exportCtx.textAlign = 'right';
      exportCtx.fillText(this.differenceMainBechmarkValue, baseWidth - padding - 80, currentY + 15);

      exportCtx.fillStyle = '#9CA3AF'; // text-gray-400
      exportCtx.fillText(formattedUnit, baseWidth - padding, currentY + 15);

      currentY += 35;

      // Draw separator line
      exportCtx.strokeStyle = '#E5E7EB'; // hr color
      exportCtx.lineWidth = 1;
      exportCtx.beginPath();
      exportCtx.moveTo(padding, currentY);
      exportCtx.lineTo(baseWidth - padding, currentY);
      exportCtx.stroke();

      currentY += 20;

      // Draw each process chain
      for (let i = 0; i < this.processChains.length; i++) {
        const chain = this.processChains[i];
        const chainTotal = chain.total || calculateTotal(chain);
        const sortedSegments = sortSegments(chain.segments);

        // Draw chain name
        exportCtx.fillStyle = '#6B7280'; // text-gray-500
        exportCtx.font = '12px Poppins';
        exportCtx.textAlign = 'left';
        exportCtx.fillText(chain.name, padding, currentY + 15);

        // Draw total below chain name
        exportCtx.fillStyle = '#6B7280'; // text-gray-500
        exportCtx.font = '12px Poppins';
        exportCtx.textAlign = 'left';
        const chainFormattedUnit = this.totalUnit.replace(/CO2|Co2|co2/gi, 'CO₂');
        exportCtx.fillText(`Total: ${chainTotal} ${chainFormattedUnit}`, padding, currentY + 32);

        // Draw chart segments
        const chartY = currentY + 45;
        const chartWidth = baseWidth - padding * 2;
        const segmentWidth = (chartWidth * chainTotal) / this.maxTotal;

        let x = padding;
        sortedSegments.forEach((segment, segIndex) => {
          const segmentValue = segment.value || 0; // Handle undefined values
          const width = (segmentValue / chainTotal) * segmentWidth;
          exportCtx.fillStyle = getSegmentColor(segment.label, this.allSegmentTypes);

          // Draw rounded rectangle
          this.drawRoundedRect(exportCtx, x, chartY, width, 20, {
            topLeft: segIndex === 0 ? 10 : 0,
            bottomLeft: segIndex === 0 ? 10 : 0,
            topRight: segIndex === sortedSegments.length - 1 ? 10 : 0,
            bottomRight: segIndex === sortedSegments.length - 1 ? 10 : 0,
          });

          x += width;
        });

        currentY += chartHeight + chartSpacing;
      }

      // Draw legend
      currentY += 20;
      exportCtx.fillStyle = '#383e46'; // text-navigation equivalent
      exportCtx.font = '12px Poppins';
      exportCtx.textAlign = 'left';
      exportCtx.fillText('Legend', padding, currentY);
      currentY += 20;

      // Draw legend items and logo on the same level
      const itemsPerRow = 2;
      const itemWidth = baseWidth / itemsPerRow;

      // Calculate available width for legend items (leaving space for logo)
      const logoMaxWidth = 80;
      const logoSpacing = 120; // Space reserved for "Calculated with" text + logo
      const legendWidth = baseWidth - logoSpacing - padding;

      this.allSegmentTypes.forEach((segmentType, index) => {
        const row = Math.floor(index / itemsPerRow);
        const col = index % itemsPerRow;
        const x = padding + col * (legendWidth / itemsPerRow);
        const y = currentY + row * 25;

        // Color box
        exportCtx.fillStyle = CUSTOM_COLOR_PALETTE[index % CUSTOM_COLOR_PALETTE.length];
        exportCtx.fillRect(x, y, 15, 15);

        // Label
        exportCtx.fillStyle = '#383e46'; // text-navigation equivalent
        exportCtx.font = '12px Poppins';
        exportCtx.textAlign = 'left';
        // Capitalize first letter of each word to match template styling
        const capitalizedSegmentType = segmentType.replace(/\b\w/g, l => l.toUpperCase());
        exportCtx.fillText(capitalizedSegmentType, x + 20, y + 12);
      });

      // Draw logo on the same line as legend items
      const logoImage = new Image();
      logoImage.src = logoUrl;
      await new Promise(resolve => (logoImage.onload = resolve));

      // Calculate proper logo dimensions maintaining aspect ratio
      const logoMaxHeight = 30;
      const logoAspectRatio = logoImage.width / logoImage.height;

      let logoDisplayWidth = logoMaxWidth;
      let logoDisplayHeight = logoMaxWidth / logoAspectRatio;

      // If height exceeds max, scale down based on height
      if (logoDisplayHeight > logoMaxHeight) {
        logoDisplayHeight = logoMaxHeight;
        logoDisplayWidth = logoMaxHeight * logoAspectRatio;
      }

      // Position logo on the right side at the same level as legend items
      const logoY = currentY + 5; // Slight adjustment to align with legend items

      // Draw text aligned with logo center
      exportCtx.font = '10px Poppins';
      exportCtx.fillStyle = '#838A93';
      exportCtx.textAlign = 'right';
      const textY = logoY + logoDisplayHeight / 2 + 3;
      exportCtx.fillText('Calculated with', baseWidth - logoDisplayWidth - 10 - padding, textY);

      exportCtx.drawImage(
        logoImage,
        baseWidth - logoDisplayWidth - padding,
        logoY,
        logoDisplayWidth,
        logoDisplayHeight
      );

      // Download
      const link = document.createElement('a');
      link.download = 'spark-co2-comparison-chart.png';
      link.href = exportCanvas.toDataURL('image/png', 1.0);
      link.click();
    },
    drawRoundedRect(ctx, x, y, width, height, radius) {
      ctx.beginPath();
      ctx.moveTo(x + radius.topLeft, y);
      ctx.lineTo(x + width - radius.topRight, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius.topRight);
      ctx.lineTo(x + width, y + height - radius.bottomRight);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius.bottomRight, y + height);
      ctx.lineTo(x + radius.bottomLeft, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bottomLeft);
      ctx.lineTo(x, y + radius.topLeft);
      ctx.quadraticCurveTo(x, y, x + radius.topLeft, y);
      ctx.closePath();
      ctx.fill();
    },
  },
};
</script>
